import React, { useEffect, useContext, useState } from 'react'
import { RadioButton } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import { rankedAddressSituationOptions } from './AddressSituationOptions'
import AppFormQuestion from '../../../AppFormQuestion'
import QuestionKey from '../../../../Questions/question-key'
import { ApplicationFormContext } from '../../../ApplicationForm/ApplicationFormContext'
import withFade from '../../../HOC/withFade'
import { trackMultipleInputChoice, determineRadioVariant, scrollToTheTop, questionFontStyle, fireReactGAEvent } from '../../utils'
import AddressHistoryEntryContext from '../AddressEntry/AddressHistoryEntryContext'
import NavigationButtons from '../../../NavigationButtons/NavigationButtons'
import { HistoryIndex } from '../../HistoryEntry/HistoryEntry'
import ToggleButtonGroup from '@zuto/atoms/src/Mui/ToogleButtonGroup/ToggleButtonGroup'
import { useAppFormEventsService } from '../../../../hooks/useAppFormEventsService'
import { AmplitudeEvents, trackAmplitudeEvent } from '../../../../utils/amplitude-utils'
import { toSnakeCase } from '../../../../utils/string-utils'

interface AddressSituationProps {
  entryId: HistoryIndex
  existingSituation?: string
  disablePrevious?: boolean
}

const AddressSituation = ({ existingSituation, entryId, disablePrevious = false }: AddressSituationProps): JSX.Element => {
  const { featureSwitches } = useContext(ApplicationFormContext)
  const eventsService = useAppFormEventsService()
  const { dispatch } = useContext(AddressHistoryEntryContext)
  const { dispatch: appFormContextDispatch } = useContext(ApplicationFormContext)
  const { removeContinueCtaV1, questionCopyChangeV1, msmThemeV1 } = featureSwitches ?? {}

  const startingAnswer = existingSituation ?? (!removeContinueCtaV1 ? 'private tenant' : undefined)
  const [currentAnswer, setCurrentAnswer] = useState(startingAnswer)

  const [nextButtonCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()

  useEffect(() => {
    const label = `qst.addressHistory.${parseInt(entryId, 10) - 1}.livingArrangements`
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Question Visible',
      label,
      nonInteraction: true
    })
    eventsService?.sendEvent('AppFormQuestionVisible', { QuestionLabel: label })
    trackAmplitudeEvent(AmplitudeEvents.ApplicationQuestionViewed, { question_label: toSnakeCase(label) })
  }, [])

  useEffect(scrollToTheTop, [])

  const optionSelected = (option: string) => {
    setCurrentAnswer(option)
    if (removeContinueCtaV1) {
      dispatch({
        type: 'UPDATE_STATUS',
        value: option
      })
      dispatch({
        type: 'NEXT'
      })
    }
  }

  const saveAnswer = () => {
    if (currentAnswer) {
      dispatch({
        type: 'UPDATE_STATUS',
        value: currentAnswer
      })
    }
  }

  const nextHandler = () => {
    if (!currentAnswer) return
    trackMultipleInputChoice({ label: `addressSituation+${currentAnswer}` })
    saveAnswer()
    dispatch({
      type: 'NEXT'
    })
  }

  const previousHandler = () => {
    saveAnswer()
    appFormContextDispatch({
      type: 'NAVIGATED_FROM_QUESTION',
      questionName: `addressLivingArrangementsStatus-${parseInt(entryId, 10)}`,
      lastNavAction: 'PREVIOUS'
    })
    dispatch({
      type: 'PREVIOUS'
    })
  }

  const renderQuestionTitle = () => {
    if (questionCopyChangeV1) {
      if (entryId !== '1') return 'What was your previous residential status?'
      return "What's your residential status?"
    }

    return 'What best describes you?'
  }

  const renderOptions = () => {
    if (msmThemeV1) {
      return (
        <ToggleButtonGroup
          options={rankedAddressSituationOptions}
          id="address-situation-options"
          data-testid="address-options"
          defaultState={startingAnswer}
          valueChanged={(value) => optionSelected(value.toString())}
        />
      )
    }
    return (
      <RadioButton
        options={rankedAddressSituationOptions}
        id="address-situation-options"
        mb={3}
        mt={2}
        direction="vertical"
        title="Show more options"
        defaultState={startingAnswer}
        valueChanged={optionSelected}
        variant={determineRadioVariant({
          smallRadiosSideBySide: false
        })}
        alignment="center"
        data-testid="address-options"
        removeContinueCta={removeContinueCtaV1}
      />
    )
  }

  return (
    <>
      <AppFormQuestion
        mt={3}
        questionKey={`${QuestionKey.ADDRESS_HISTORY}.livingArrangements`}
        textStyle={questionFontStyle}
      >
        {renderQuestionTitle()}
      </AppFormQuestion>
      {renderOptions()}
      <NavigationButtons
        nextHandler={() => nextButtonCb(nextHandler)}
        previousHandler={() => previousButtonCb(previousHandler)}
        disableNext={!currentAnswer}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        hideNext={removeContinueCtaV1}
      />
    </>
  )
}

export default withFade(AddressSituation)
